/**
 * The Brand panel: the workspace brand kit (ADR 0030). Everyone applies it: a colour to the selection (text colour,
 * fill, line), a text style to selected text, a logo as a new image. People with PRISM's asset.edit_metadata also
 * change it: add the selection's colour or text style, library fonts and logos, and remove entries.
 */
import { useEffect, useState, useSyncExternalStore } from 'react';
import { ImagePlus, Plus, X } from 'lucide-react';
import type { TextElement } from '@prism-graphics/scene-schema';
import type { BrandKit } from '../api.ts';
import { t } from '../i18n.ts';
import { applyColor, applyTextStyle, textStyleOf } from '../model/brand.ts';
import { findElement } from '../model/tree.ts';
import { addElement } from '../state/actions.ts';
import { assetCache } from '../state/assets.ts';
import { brandCache } from '../state/brand.ts';
import { sceneView, useEditor, type EditorStore } from '../state/store.ts';
import { IconButton, Section, SelectField } from '../ui/controls.tsx';

export function BrandPanel({ store }: { store: EditorStore }) {
  useSyncExternalStore(brandCache.subscribe, brandCache.getVersion);
  useSyncExternalStore(assetCache.subscribe, assetCache.getVersion);
  const scene = useEditor(store, sceneView);
  const selection = useEditor(store, (s) => s.selection);
  const [newColor, setNewColor] = useState('#00B4C8');
  const [newName, setNewName] = useState('');
  useEffect(() => {
    void brandCache.load();
  }, []);
  const stored = brandCache.stored;
  const kit = brandCache.kit;
  const canEdit = stored?.canEdit ?? false;
  const lib = (hash: string) => assetCache.byHash(hash);
  const logos = kit.logos.map(lib).filter((a) => a !== undefined);
  useEffect(() => {
    assetCache.ensure(logos.map((a) => a.sceneAsset));
  });
  const save = (next: Partial<BrandKit>) => void brandCache.save({ ...kit, ...next });
  const selectedText = selection.map((id) => findElement(scene, id)).filter((e): e is TextElement => e?.type === 'text');
  const fontName = (hash: string) => {
    const a = lib(hash);
    return a ? `${a.meta.family ?? a.originalName}${a.meta.subfamily && a.meta.subfamily !== 'Regular' ? ` ${a.meta.subfamily}` : ''}` : hash.slice(7, 15);
  };

  if (!stored && !brandCache.error) return <p className="hint pad">{t('editor.loading')}</p>;
  return (
    <div className="inspector brand-panel" data-testid="brand-panel">
      <h2 className="panel-title">{t('brand.title')}</h2>
      <p className="hint pad">{canEdit ? t('brand.hintEdit') : t('brand.hintView')}</p>
      {brandCache.error && (
        <p className="hint warn pad" role="alert">
          {brandCache.error}
        </p>
      )}

      <Section title={t('brand.colors')} testId="brand-colors">
        {kit.colors.length === 0 && <p className="hint">{t('brand.noColors')}</p>}
        <ul className="swatches">
          {kit.colors.map((c, i) => (
            <li key={`${c.name}-${i}`} className="swatch-row">
              <button
                type="button"
                className="swatch"
                style={{ background: c.value }}
                title={t('brand.applyColor', { name: c.name })}
                aria-label={t('brand.applyColor', { name: c.name })}
                disabled={selection.length === 0}
                onClick={() => store.apply((s) => applyColor(s, selection, c.value))}
                data-testid="brand-color"
              />
              <span className="swatch-name">{c.name}</span>
              <code className="muted">{c.value}</code>
              {canEdit && (
                <IconButton label={t('brand.remove', { name: c.name })} onClick={() => save({ colors: kit.colors.filter((_, j) => j !== i) })}>
                  <X size={12} />
                </IconButton>
              )}
            </li>
          ))}
        </ul>
        {canEdit && (
          <form
            className="brand-add"
            onSubmit={(e) => {
              e.preventDefault();
              if (!newName.trim()) return;
              save({ colors: [...kit.colors, { name: newName.trim(), value: newColor.toUpperCase() }] });
              setNewName('');
            }}
          >
            <input type="color" value={newColor.slice(0, 7)} onChange={(e) => setNewColor(e.target.value)} aria-label={t('brand.newColor')} data-testid="brand-new-color" />
            <input type="text" value={newName} placeholder={t('brand.colorName')} onChange={(e) => setNewName(e.target.value)} aria-label={t('brand.colorName')} data-testid="brand-new-color-name" />
            <button type="submit" className="icon-btn" aria-label={t('brand.addColor')} title={t('brand.addColor')} data-testid="brand-add-color">
              <Plus size={14} />
            </button>
          </form>
        )}
      </Section>

      <Section title={t('brand.textStyles')} testId="brand-text-styles">
        {kit.textStyles.length === 0 && <p className="hint">{t('brand.noTextStyles')}</p>}
        <ul className="list">
          {kit.textStyles.map((st, i) => (
            <li key={`${st.name}-${i}`} className="draft-row">
              <button
                type="button"
                className="list-item"
                disabled={selectedText.length === 0}
                title={selectedText.length ? t('brand.applyStyle', { name: st.name }) : t('brand.selectText')}
                onClick={() => {
                  const font = lib(st.font)?.sceneAsset ?? scene.assets.find((a) => a.hash === st.font);
                  store.apply((s) => applyTextStyle(s, selectedText.map((x) => x.id), st, font));
                }}
                data-testid="brand-text-style"
              >
                <span className="li-title" style={{ fontWeight: st.fontWeight, textTransform: st.textTransform }}>
                  {st.name}
                </span>
                <span className="li-meta">
                  {fontName(st.font)} · {st.fontWeight} · {st.fontSize}px
                </span>
              </button>
              {canEdit && (
                <IconButton label={t('brand.remove', { name: st.name })} onClick={() => save({ textStyles: kit.textStyles.filter((_, j) => j !== i) })}>
                  <X size={12} />
                </IconButton>
              )}
            </li>
          ))}
        </ul>
        {canEdit && selectedText.length === 1 && (
          <button
            type="button"
            className="btn small"
            data-testid="brand-save-style"
            onClick={() => {
              const name = window.prompt(t('brand.styleNamePrompt'), selectedText[0]!.name || t('brand.styleDefaultName'));
              if (name?.trim()) save({ textStyles: [...kit.textStyles, textStyleOf(selectedText[0]!, name.trim())] });
            }}
          >
            {t('brand.saveStyle')}
          </button>
        )}
      </Section>

      <Section title={t('brand.logos')} testId="brand-logos">
        {kit.logos.length === 0 && <p className="hint">{t('brand.noLogos')}</p>}
        <ul className="logo-grid">
          {logos.map((a) => (
            <li key={a.hash}>
              <button type="button" className="logo-tile" title={t('brand.addLogo', { name: a.originalName })} onClick={() => addElement(store, 'image', { asset: a })} data-testid="brand-logo">
                <img src={assetCache.imageUrl(a.hash)} alt={a.originalName} />
                <ImagePlus size={14} aria-hidden className="logo-add" />
              </button>
              {canEdit && (
                <IconButton label={t('brand.remove', { name: a.originalName })} onClick={() => save({ logos: kit.logos.filter((h) => h !== a.hash) })}>
                  <X size={12} />
                </IconButton>
              )}
            </li>
          ))}
        </ul>
        {canEdit && (
          <SelectField
            label={t('brand.addFromLibrary')}
            value=""
            testId="brand-add-logo"
            options={[{ value: '', label: '–' }, ...assetCache.library.filter((a) => a.kind === 'image' && !kit.logos.includes(a.hash)).map((a) => ({ value: a.hash, label: a.originalName }))]}
            onChange={(hash) => hash && save({ logos: [...kit.logos, hash] })}
          />
        )}
      </Section>

      <Section title={t('brand.fonts')} testId="brand-fonts">
        {kit.fonts.length === 0 && <p className="hint">{t('brand.noFonts')}</p>}
        <ul className="list plain">
          {kit.fonts.map((h) => (
            <li key={h} className="draft-row">
              <span className="li-title">{fontName(h)}</span>
              {canEdit && (
                <IconButton label={t('brand.remove', { name: fontName(h) })} onClick={() => save({ fonts: kit.fonts.filter((x) => x !== h) })}>
                  <X size={12} />
                </IconButton>
              )}
            </li>
          ))}
        </ul>
        <p className="hint">{t('brand.fontsHint')}</p>
        {canEdit && (
          <SelectField
            label={t('brand.addFromLibrary')}
            value=""
            testId="brand-add-font"
            options={[{ value: '', label: '–' }, ...assetCache.library.filter((a) => a.kind === 'font' && !kit.fonts.includes(a.hash)).map((a) => ({ value: a.hash, label: fontName(a.hash) }))]}
            onChange={(hash) => hash && save({ fonts: [...kit.fonts, hash] })}
          />
        )}
      </Section>
    </div>
  );
}
